import React, { useState, useEffect } from 'react';
import {
  CalculatorIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  CubeTransparentIcon,
  PencilIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface MathInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: 'problem-solving' | 'graph-analysis' | 'proof-writing' | 'statistical-calculation' | 'geometric-construction';
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const MathInteractive: React.FC<MathInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Tracking states
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(180); // 3 minutes for math
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your mathematical work.', {
          icon: '🧮',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    switch (exerciseType) {
      case 'problem-solving':
        setInputs({
          solution: '',
          steps: ['', '', ''],
          finalAnswer: '',
          verification: ''
        });
        break;
      case 'graph-analysis':
        setInputs({
          interpretation: '',
          keyFeatures: [],
          calculations: '',
          conclusions: ''
        });
        break;
      case 'proof-writing':
        setInputs({
          theorem: '',
          given: '',
          prove: '',
          proofSteps: ['', '', '', ''],
          conclusion: ''
        });
        break;
      case 'statistical-calculation':
        setInputs({
          dataAnalysis: '',
          calculations: '',
          mean: '',
          median: '',
          standardDeviation: '',
          interpretation: ''
        });
        break;
      case 'geometric-construction':
        setInputs({
          construction: '',
          steps: ['', '', ''],
          justification: '',
          properties: ''
        });
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes working on the problem.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'problem-solving':
        return ['solution', 'finalAnswer'];
      case 'graph-analysis':
        return ['interpretation'];
      case 'proof-writing':
        return ['theorem', 'conclusion'];
      case 'statistical-calculation':
        return ['calculations', 'interpretation'];
      case 'geometric-construction':
        return ['construction', 'justification'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'problem-solving':
        return evaluateProblemSolving();
      case 'graph-analysis':
        return evaluateGraphAnalysis();
      case 'proof-writing':
        return evaluateProof();
      case 'statistical-calculation':
        return evaluateStatistics();
      case 'geometric-construction':
        return evaluateGeometry();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateProblemSolving = () => {
    let score = 0;
    const feedback = [];

    // Solution approach (30 points)
    if (inputs.solution?.length > 50) {
      score += 30;
      feedback.push('✓ Solution approach demonstrated');
    }

    // Step-by-step work (40 points)
    const validSteps = inputs.steps?.filter((step: string) => step.length > 15).length || 0;
    score += Math.min(40, validSteps * 13.33);
    if (validSteps >= 2) {
      feedback.push('✓ Step-by-step work shown');
    }

    // Final answer (20 points)
    if (inputs.finalAnswer) {
      score += 20;
      feedback.push('✓ Final answer provided');
    }

    // Verification (10 points)
    if (inputs.verification?.length > 20) {
      score += 10;
      feedback.push('✓ Answer verification included');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateGraphAnalysis = () => {
    let score = 0;
    const feedback = [];

    // Graph interpretation (40 points)
    if (inputs.interpretation?.length > 60) {
      score += 40;
      feedback.push('✓ Graph interpretation provided');
    }

    // Key features identification (30 points)
    if (inputs.keyFeatures?.length >= 2) {
      score += 30;
      feedback.push('✓ Key features identified');
    }

    // Mathematical calculations (30 points)
    if (inputs.calculations?.length > 30) {
      score += 30;
      feedback.push('✓ Calculations performed');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateProof = () => {
    let score = 0;
    const feedback = [];

    // Theorem statement (15 points)
    if (inputs.theorem?.length > 20) {
      score += 15;
      feedback.push('✓ Theorem clearly stated');
    }

    // Logical proof steps (50 points)
    const validSteps = inputs.proofSteps?.filter((step: string) => step.length > 20).length || 0;
    score += Math.min(50, validSteps * 12.5);
    if (validSteps >= 3) {
      feedback.push('✓ Logical proof steps provided');
    }

    // Valid conclusion (35 points)
    if (inputs.conclusion?.length > 30) {
      score += 35;
      feedback.push('✓ Conclusion reached');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateStatistics = () => {
    let score = 0;
    const feedback = [];

    // Data analysis (25 points)
    if (inputs.dataAnalysis?.length > 40) {
      score += 25;
      feedback.push('✓ Data analysis provided');
    }

    // Statistical calculations (40 points)
    const statsProvided = [inputs.mean, inputs.median, inputs.standardDeviation].filter(s => s && s.length > 0).length;
    score += Math.min(40, statsProvided * 13.33);

    // Interpretation (35 points)
    if (inputs.interpretation?.length > 50) {
      score += 35;
      feedback.push('✓ Statistical interpretation provided');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateGeometry = () => {
    let score = 0;
    const feedback = [];

    // Construction description (30 points)
    if (inputs.construction?.length > 50) {
      score += 30;
      feedback.push('✓ Construction described');
    }

    // Construction steps (40 points)
    const validSteps = inputs.steps?.filter((step: string) => step.length > 15).length || 0;
    score += Math.min(40, validSteps * 13.33);

    // Mathematical justification (30 points)
    if (inputs.justification?.length > 40) {
      score += 30;
      feedback.push('✓ Mathematical justification provided');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent mathematical work! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'problem-solving': return <CalculatorIcon className="h-6 w-6" />;
      case 'graph-analysis': return <ChartBarIcon className="h-6 w-6" />;
      case 'proof-writing': return <PencilIcon className="h-6 w-6" />;
      case 'statistical-calculation': return <DocumentTextIcon className="h-6 w-6" />;
      case 'geometric-construction': return <CubeTransparentIcon className="h-6 w-6" />;
      default: return <CalculatorIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'problem-solving':
        return (
          <div className="space-y-6">
            {scenario?.problem && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Problem:</h4>
                <div className="text-sm text-gray-700 font-mono whitespace-pre-wrap">{scenario.problem}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Solution Approach *
              </label>
              <textarea
                value={inputs.solution || ''}
                onChange={(e) => handleInputChange('solution', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="Describe your approach to solving this problem"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Step-by-Step Solution
              </label>
              {[0, 1, 2].map((index) => (
                <textarea
                  key={index}
                  value={inputs.steps?.[index] || ''}
                  onChange={(e) => {
                    const newSteps = [...(inputs.steps || ['', '', ''])];
                    newSteps[index] = e.target.value;
                    handleInputChange('steps', newSteps);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 mb-2 font-mono"
                  rows={2}
                  placeholder={`Step ${index + 1}: Show your work`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Final Answer *
              </label>
              <input
                type="text"
                value={inputs.finalAnswer || ''}
                onChange={(e) => handleInputChange('finalAnswer', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 font-mono"
                placeholder="Enter your final answer"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification/Check
              </label>
              <textarea
                value={inputs.verification || ''}
                onChange={(e) => handleInputChange('verification', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 font-mono"
                rows={2}
                placeholder="How can you verify your answer is correct?"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="mb-4">
              {getExerciseIcon()}
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {exerciseType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} Exercise
            </h4>
            <p className="text-gray-600 mb-6">
              This mathematics exercise is being developed with specialized mathematical tools and assessments.
            </p>
            
            <button
              onClick={() => onComplete(true, 85)}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              Complete Exercise (Temporary)
            </button>
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-indigo-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum problem-solving time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-indigo-600 hover:text-indigo-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm font-mono">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              Submit Solution
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MathInteractive;